// ============================================================
// LLAbrController — ABR controller for the LL-HLS engine
// ============================================================
//
// Wraps the stock hls.js ABR controller instead of replacing it: every
// emergency path hls.js owns (the abandon rules that abort an in-flight
// fragment, error-driven level demotion, starvation handling) keeps running
// untouched. What this subclass adds is a second opinion on top of it:
//
//   * a throughput estimator that measures the network instead of the LL
//     delivery pacing (see throughputEstimator.ts),
//   * a bitrate-only selection policy that can cross codec families, which the
//     stock findBestLevel refuses to do (see abrPolicy.ts),
//   * an occasional saturating probe so a fast connection can prove it is fast
//     while only ~400 ms parts are flowing (see bandwidthProbe.ts).
//
// The subclass is built lazily by `createLLAbrController` so that the base class
// is read from `Hls.DefaultConfig` at engine-construction time; test doubles for
// hls.js that do not expose an ABR controller simply get no custom ABR.
import Hls, { type AbrController } from 'hls.js';
import type { Logger } from '../../core/Logger';
import { ThroughputEstimator } from './throughputEstimator';
import {
  AbrPolicy,
  type AbrLadderLevel,
  type AbrPolicyInput,
  type AbrPolicyOptions,
} from './abrPolicy';
import { probeThroughput } from './bandwidthProbe';
import { BufferTrendTracker } from './bufferTrend';

export interface LLAbrControllerOptions {
  logger: Logger;
  /** Current adaptive LL latency target, in seconds. */
  getLatencyTarget: () => number;
  /** CDN auth query string, without the leading separator. */
  getQueryParams: () => string;
  /** True while the engine is rebuilding buffer headroom after a stall. */
  isRecoveringBuffer: () => boolean;
  /** Disables the saturating probe (traffic-sensitive deployments). */
  probeEnabled?: boolean;
  policy?: AbrPolicyOptions;
}

/** Dropped-frame share over one buffered fragment that counts as decoder overload. */
const DROPPED_FRAME_RATIO = 0.15;
/** Minimum frames in a sample before the dropped-frame ratio means anything. */
const DROPPED_FRAME_MIN_TOTAL = 60;
/**
 * Frames dropped right after a level switch come from the decoder re-initialising
 * (and, across codec families, from a SourceBuffer changeType) — not from the
 * rendition being too heavy. Blaming the level for those would put the top rung
 * on probation every time it was tried.
 */
const DROPPED_FRAME_GRACE_MS = 3_000;
/** Consecutive bad samples before a level is called too heavy for the decoder. */
const DROPPED_FRAME_STRIKES = 2;
/** Extra up-switch margin for a rendition MediaCapabilities calls not smooth. */
const NOT_SMOOTH_HEADROOM = 1.4;
/** Playback rate above which the latency controller is catching up. */
const CATCH_UP_RATE_THRESHOLD = 1.001;

function withAuth(url: string, queryParams: string): string {
  if (!queryParams) return url;
  return url + (url.includes('?') ? '&' : '?') + queryParams;
}

/** True when the platform can retype a SourceBuffer, i.e. change codec family mid-stream. */
export function canChangeCodec(): boolean {
  return (
    typeof SourceBuffer !== 'undefined' &&
    typeof SourceBuffer.prototype?.changeType === 'function'
  );
}

/**
 * Builds the ABR controller class. Returns undefined when the hls.js build in
 * use does not expose its default ABR controller (test doubles), in which case
 * the caller should leave `abrController` unset and keep stock behaviour.
 */
export function createLLAbrController(
  options: LLAbrControllerOptions,
): (new (hls: Hls) => AbrController) | undefined {
  const BaseAbrController = (Hls as unknown as {
    DefaultConfig?: { abrController?: new (hls: Hls) => unknown };
  }).DefaultConfig?.abrController;
  if (typeof BaseAbrController !== 'function') return undefined;

  // Typed against the real class (type-only import, so hls.js test doubles are
  // unaffected) while the runtime value comes from DefaultConfig.
  const Base = BaseAbrController as unknown as new (hls: Hls) => AbrController;

  return class LLAbrController extends Base {
    private readonly llHls: Hls;
    private readonly estimator = new ThroughputEstimator();
    private readonly policy = new AbrPolicy(options.policy);
    private lastReportedLevel = -1;
    private lastReason = '';
    private lastLadderSignature = '';
    private lastSnapshot: ReturnType<ThroughputEstimator['snapshot']> | null = null;
    private probeInFlight = false;
    private lastQuality: { dropped: number; total: number } | null = null;
    private levelSwitchedAtMs = 0;
    private readonly bufferTrend = new BufferTrendTracker();
    private droppedFrameStrikes = 0;
    private destroyed = false;

    constructor(hls: Hls) {
      super(hls);
      this.llHls = hls;
      this.bindOwnListeners();
    }

    // --- hls.js lifecycle -------------------------------------------------

    override destroy(): void {
      this.destroyed = true;
      this.unbindOwnListeners();
      super.destroy();
    }

    private bindOwnListeners(): void {
      const hls = this.llHls;
      hls.on(Hls.Events.FRAG_LOADED, this.onFragLoadedSample);
      hls.on(Hls.Events.FRAG_BUFFERED, this.onFragBufferedTick);
      hls.on(Hls.Events.LEVEL_SWITCHED, this.onLevelSwitchedTrack);
      hls.on(Hls.Events.ERROR, this.onErrorTrack);
      hls.on(Hls.Events.MANIFEST_LOADING, this.onManifestLoadingReset);
      hls.on(Hls.Events.FRAG_LOAD_EMERGENCY_ABORTED, this.onAbortTrack);
    }

    private unbindOwnListeners(): void {
      const hls = this.llHls;
      hls.off(Hls.Events.FRAG_LOADED, this.onFragLoadedSample);
      hls.off(Hls.Events.FRAG_BUFFERED, this.onFragBufferedTick);
      hls.off(Hls.Events.LEVEL_SWITCHED, this.onLevelSwitchedTrack);
      hls.off(Hls.Events.ERROR, this.onErrorTrack);
      hls.off(Hls.Events.MANIFEST_LOADING, this.onManifestLoadingReset);
      hls.off(Hls.Events.FRAG_LOAD_EMERGENCY_ABORTED, this.onAbortTrack);
    }

    // --- Measurement ------------------------------------------------------

    /**
     * Feeds every completed transfer — video and audio, parts and segments —
     * into the estimator. Timing starts at the first received byte, so an LL
     * blocking request that waited for the part to be produced contributes only
     * its transfer, never the wait.
     */
    private onFragLoadedSample = (_event: unknown, data: any): void => {
      const stats = data?.part ? data.part.stats : data?.frag?.stats;
      const loading = stats?.loading;
      if (!loading || !stats?.loaded) return;
      this.estimator.add({
        startMs: loading.first || loading.start,
        endMs: loading.end || performance.now(),
        bytes: stats.loaded,
        kind: data?.part ? 'part' : 'segment',
      });
    };

    private onFragBufferedTick = (_event: unknown, data: any): void => {
      // FRAG_BUFFERED also fires for alt-audio fragments, whose `level` is an
      // audio track index — never a rung of the video ladder. Frame drops are
      // always a property of the video level currently being loaded.
      if (data?.frag?.type === 'main') {
        this.checkDroppedFrames(this.llHls.loadLevel);
      }
      void this.maybeProbe();
    };

    /**
     * A rendition the decoder cannot keep up with looks perfect on the network
     * but drops frames. Treat that as an incident against the active level so it
     * goes on probation instead of being re-selected forever.
     */
    private checkDroppedFrames(level: number): void {
      const media = this.llHls.media as HTMLVideoElement | null;
      if (!media || typeof media.getVideoPlaybackQuality !== 'function') return;
      const q = media.getVideoPlaybackQuality();
      const dropped = q.droppedVideoFrames ?? 0;
      const total = q.totalVideoFrames ?? 0;
      const previous = this.lastQuality;
      this.lastQuality = { dropped, total };
      if (!previous) return;
      const deltaTotal = total - previous.total;
      const deltaDropped = dropped - previous.dropped;
      if (deltaTotal < DROPPED_FRAME_MIN_TOTAL) return;
      if (deltaDropped / deltaTotal < DROPPED_FRAME_RATIO) {
        this.droppedFrameStrikes = 0;
        return;
      }
      const now = performance.now();
      if (now - this.levelSwitchedAtMs < DROPPED_FRAME_GRACE_MS) return;
      if (++this.droppedFrameStrikes < DROPPED_FRAME_STRIKES) return;
      this.droppedFrameStrikes = 0;
      options.logger.warn(
        `[abr] level ${level} dropping frames (${deltaDropped}/${deltaTotal}) — probation`,
      );
      this.policy.noteIncident('dropped-frames', level, now);
    }

    private onLevelSwitchedTrack = (_event: unknown, data: any): void => {
      if (typeof data?.level === 'number') {
        this.policy.noteLevelActive(data.level, performance.now());
      }
      // Frame counters are per-decoder-run; a switch invalidates the baseline.
      this.lastQuality = null;
      this.droppedFrameStrikes = 0;
      this.levelSwitchedAtMs = performance.now();
      // The switch itself moves the buffer, so the trend measured across it
      // describes the switch rather than delivery.
      this.bufferTrend.reset();
    };

    private onErrorTrack = (_event: unknown, data: any): void => {
      const now = performance.now();
      const details: string = data?.details ?? '';
      const level =
        typeof data?.frag?.level === 'number' ? data.frag.level : this.llHls.loadLevel;
      if (details === 'bufferStalledError') {
        this.policy.noteIncident('stall', level, now);
      } else if (
        details === 'fragLoadError' ||
        details === 'fragLoadTimeOut' ||
        details === 'fragParsingError' ||
        details === 'bufferAppendError'
      ) {
        this.policy.noteIncident('fragment-error', level, now);
      }
    };

    private onAbortTrack = (_event: unknown, data: any): void => {
      const level =
        typeof data?.frag?.level === 'number' ? data.frag.level : this.llHls.loadLevel;
      this.policy.noteIncident('abandon', level, performance.now());
    };

    private onManifestLoadingReset = (): void => {
      this.estimator.reset();
      this.policy.reset();
      this.lastReportedLevel = -1;
      this.lastQuality = null;
      this.bufferTrend.reset();
    };

    // --- Selection --------------------------------------------------------

    private bufferSeconds(): number {
      const info = (this.llHls as any).mainForwardBufferInfo;
      if (info && Number.isFinite(info.len)) return info.len;
      const media = this.llHls.media;
      if (!media) return 0;
      const { buffered, currentTime } = media;
      for (let i = 0; i < buffered.length; i++) {
        if (currentTime >= buffered.start(i) && currentTime <= buffered.end(i)) {
          return buffered.end(i) - currentTime;
        }
      }
      return 0;
    }

    private ladder(): AbrLadderLevel[] {
      const current = this.llHls.loadLevel;
      const reasons: string[] = [];
      const ladder = this.llHls.levels.map((level, index) => {
        // MediaCapabilities can report a rendition as decodable but not smooth
        // (1440p60 HEVC without hardware decode, say). Selecting it would trade
        // resolution for dropped frames, so it is treated as unavailable.
        const notSmooth = !!level.supportedResult?.decodingInfoResults?.some(
          info => info.smooth === false,
        );
        const unsupported = !!level.supportedResult?.error;
        // Error history disqualifies a level we are not already playing — the
        // same rule findBestLevel applies, and hls.js clears the counters once a
        // fragment from that level buffers.
        const errored =
          index !== current && (level.loadError > 0 || level.fragmentError > 0);
        // `notSmooth` is a prediction, not a measurement, and a wrong one costs
        // the whole top of the ladder. Keep the level selectable but make it
        // earn a wider margin; the dropped-frame guard demotes it if the
        // prediction was right.
        const eligible = !unsupported && !errored;
        reasons.push(
          `${index}:${level.height}p/${level.codecSet}=` +
            (eligible
              ? 'ok'
              : unsupported
                ? 'unsupported'
                : `errors(${level.loadError}/${level.fragmentError})`) +
            (notSmooth ? '+not-smooth' : ''),
        );
        return {
          index,
          bitrate: level.averageBitrate || level.bitrate,
          codecSet: level.codecSet,
          height: level.height,
          eligible,
          penalty: notSmooth ? NOT_SMOOTH_HEADROOM : 1,
        };
      });

      // Eligibility is the one input that can silently pin auto mode to a rung,
      // so surface it whenever it changes rather than only on a level switch.
      // Bitrates are deliberately left out of the signature: realBitrate moves
      // with every fragment and would make this log fire continuously.
      const signature = reasons.join(' ');
      if (signature !== this.lastLadderSignature) {
        this.lastLadderSignature = signature;
        options.logger.debug('[abr]', `ladder ${signature}`);
      }
      return ladder;
    }

    private policyInput(baseLevel: number, forcedLevel: number): AbrPolicyInput {
      const nowMs = performance.now();
      const buffer = this.bufferSeconds();
      const snapshot = this.estimator.snapshot(nowMs);
      this.lastSnapshot = snapshot;
      return {
        nowMs,
        levels: this.ladder(),
        currentLevel: this.llHls.loadLevel,
        baseLevel,
        estimateBps: snapshot.bps,
        saturatedBps: snapshot.burstBps,
        recentBps: snapshot.recentBps,
        estimateReady: snapshot.ready,
        bufferSeconds: buffer,
        bufferTrendPerSecond: this.bufferTrend.sample({
          bufferSeconds: buffer,
          currentTime: this.llHls.media?.currentTime ?? 0,
          playbackRate: this.llHls.media?.playbackRate ?? 1,
          nowMs,
        }),
        latencyTargetSeconds: options.getLatencyTarget(),
        forcedLevel,
        crossCodecAllowed: canChangeCodec(),
        recoveringBuffer: options.isRecoveringBuffer(),
      };
    }

    override get nextAutoLevel(): number {
      // Read the emergency force first: the base getter recomputes its own
      // choice and clears `_nextAutoLevel` as a side effect, so asking afterwards
      // reports -1 even when the abandon rules had just forced a level down.
      const forcedLevel = this.forcedAutoLevel;
      const base = super.nextAutoLevel;
      if (this.destroyed || !this.llHls.levels?.length) return base;

      const input = this.policyInput(base, forcedLevel);
      const decision = this.policy.decide(input);
      const level = Math.min(
        Math.max(decision.level, this.llHls.minAutoLevel),
        this.llHls.maxAutoLevel,
      );

      if (level !== this.lastReportedLevel || decision.reason !== this.lastReason) {
        this.lastReportedLevel = level;
        this.lastReason = decision.reason;
        const height = this.llHls.levels[level]?.height ?? '?';
        options.logger.debug(
          '[abr]',
          `${decision.reason}: level ${level} (${height}p)` +
            ` hls=${base} bw=${Math.round(input.estimateBps / 1000)}kbps` +
            ` sat=${Math.round(input.saturatedBps / 1000)}kbps` +
            ` recent=${Math.round(input.recentBps / 1000)}kbps` +
            ` samples=${this.lastSnapshot?.sampleCount ?? 0}` +
            ` buffer=${input.bufferSeconds.toFixed(2)}s` +
            ` trend=${input.bufferTrendPerSecond.toFixed(2)}/s`,
        );
      }
      return level;
    }

    override set nextAutoLevel(value: number) {
      super.nextAutoLevel = value;
    }

    // --- Probing ----------------------------------------------------------

    /**
     * Spends one probe when the next rung up is plausibly affordable but cannot
     * be proven from part-sized transfers alone. Rate limiting lives in the
     * policy; this only performs the measurement and feeds it back.
     */
    private async maybeProbe(): Promise<void> {
      if (options.probeEnabled === false) return;
      if (this.probeInFlight || this.destroyed) return;
      if (!this.llHls.autoLevelEnabled) return;
      // While the latency controller is catching up it is trying to refill the
      // buffer at 1.08x. A half-megabyte probe competing with that would deepen
      // the very deficit it is closing.
      const media = this.llHls.media;
      if (media && media.playbackRate > CATCH_UP_RATE_THRESHOLD) return;

      const input = this.policyInput(this.llHls.loadLevel, this.forcedAutoLevel);
      if (!input.estimateReady) return;
      const target = this.policy.probeTarget(input);
      if (target === null) return;

      const url = this.llHls.levels[target]?.url?.[0];
      if (!url) return;

      const queryParams = options.getQueryParams();
      this.probeInFlight = true;
      this.policy.noteProbe(input.nowMs);
      try {
        const result = await probeThroughput({
          playlistUrl: withAuth(url, queryParams),
          withAuth: u => withAuth(u, queryParams),
        });
        if (this.destroyed) return;
        if (!result) {
          // A silent failure here looks exactly like "auto never goes up", so it
          // must be visible: no probe means no saturating evidence, which means
          // a codec-family change can never be justified.
          options.logger.debug('[abr]', `probe level ${target}: no usable measurement`);
          return;
        }
        this.estimator.add({ ...result, kind: 'probe' });
        const bps = (8 * result.bytes * 1000) / (result.endMs - result.startMs);
        options.logger.debug(
          '[abr]',
          `probe level ${target}: ${Math.round(bps / 1000)}kbps` +
            ` over ${Math.round(result.endMs - result.startMs)}ms`,
        );
      } finally {
        this.probeInFlight = false;
      }
    }
  };
}
